import { useEffect } from 'react'
import { Link } from '@inertiajs/react'
import ScrollReveal from '@/components/marketing/ScrollReveal'
import BlogPostCard, { type BlogCardPost, formatDate } from '@/components/marketing/BlogPostCard'
import { track } from '@/lib/analytics'
import type { SharedMarketingProps } from '@/types/marketing'

interface PostTag {
  name: string
  slug: string
}

interface PostDetail {
  slug: string
  title: string
  description: string
  author: string
  published_at: string
  category_name: string | null
  category_slug: string | null
  tags: PostTag[]
  reading_time: string
  body_html: string
  featured_image_url: string | null
}

interface BlogPostPageProps extends SharedMarketingProps {
  post: PostDetail
  related_posts: BlogCardPost[]
}

const SHARE_CHANNELS: { channel: string; label: string }[] = [
  { channel: 'twitter', label: 'X' },
  { channel: 'facebook', label: 'Facebook' },
  { channel: 'linkedin', label: 'LinkedIn' },
  { channel: 'copy_link', label: 'Copy link' },
]

export default function BlogPost({ post, related_posts }: BlogPostPageProps) {
  // view_blog_post — fire once on mount.
  useEffect(() => {
    track('view_blog_post', {
      data: { slug: post.slug, category: post.category_slug, tags: post.tags.map((t) => t.slug) },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function shareTo(channel: string) {
    track('share_blog_post', { data: { slug: post.slug, channel } })
    const url = window.location.href
    if (channel === 'copy_link') {
      navigator.clipboard?.writeText(url)
      return
    }
    const targets: Record<string, string> = {
      twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(post.title)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
    }
    const target = targets[channel]
    if (target) window.open(target, '_blank', 'noopener,noreferrer')
  }

  return (
    <>
      {/* Article header */}
      <section className="marketing-hero-section">
        <div className="container mx-auto px-6 pb-10 pt-10 lg:pb-12 lg:pt-14">
          <div className="mx-auto max-w-3xl">
            <ScrollReveal>
              <nav className="mb-6 flex items-center gap-2 text-sm text-white/40" aria-label="Breadcrumb">
                <Link href="/blogs" className="transition-colors hover:text-white" prefetch>Blog</Link>
                {post.category_name && post.category_slug && (
                  <>
                    <span aria-hidden="true">/</span>
                    <Link
                      href={`/blogs/category/${post.category_slug}`}
                      className="transition-colors hover:text-white"
                      prefetch
                    >
                      {post.category_name}
                    </Link>
                  </>
                )}
              </nav>
            </ScrollReveal>
            {post.category_name && post.category_slug && (
              <ScrollReveal delay={0.05}>
                <Link
                  href={`/blogs/category/${post.category_slug}`}
                  className="inline-block rounded-full bg-[#ff6a1a]/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-[#ff6a1a] transition-colors hover:bg-[#ff6a1a]/20"
                  prefetch
                >
                  {post.category_name}
                </Link>
              </ScrollReveal>
            )}
            <ScrollReveal delay={0.1}>
              <h1 className="mt-4 text-3xl font-bold leading-tight text-white lg:text-4xl">{post.title}</h1>
            </ScrollReveal>
            <ScrollReveal delay={0.15}>
              <div className="mt-4 flex items-center gap-3 text-sm text-white/48">
                <span>By {post.author}</span>
                <span aria-hidden="true">·</span>
                <time>{formatDate(post.published_at)}</time>
                <span aria-hidden="true">·</span>
                <span>{post.reading_time}</span>
              </div>
            </ScrollReveal>
            <ScrollReveal delay={0.2}>
              <div className="mt-5 flex flex-wrap items-center gap-2">
                <span className="mr-1 text-sm font-medium text-white/40">Share</span>
                {SHARE_CHANNELS.map(({ channel, label }) => (
                  <button
                    key={channel}
                    type="button"
                    onClick={() => shareTo(channel)}
                    aria-label={channel === 'copy_link' ? 'Copy link' : `Share on ${label}`}
                    className="rounded-full border border-white/10 px-3 py-1 text-xs font-medium text-white/64 transition-colors hover:border-[#ff6a1a]/30 hover:text-[#ff6a1a]"
                  >
                    {label}
                  </button>
                ))}
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Featured image */}
      {post.featured_image_url && (
        <section className="container mx-auto px-6 py-8">
          <ScrollReveal delay={0.2}>
            <div className="mx-auto max-w-3xl overflow-hidden rounded-2xl">
              {/* alt = post title so the image is indexable; width/height
                  reserve the slot pre-load (CLS) — 1200x630 is the standard
                  featured/OG ratio, the browser corrects to the intrinsic
                  ratio once loaded. */}
              <img
                src={post.featured_image_url}
                alt={post.title}
                width={1200}
                height={630}
                className="h-auto w-full object-cover"
                loading="eager"
              />
            </div>
          </ScrollReveal>
        </section>
      )}

      {/* Article body — rendered without ScrollReveal: a long post is far
          taller than the viewport, so the 15% in-view threshold never trips
          and the whole body would stay at opacity 0. */}
      <section className="container mx-auto px-6 py-12">
        <article
          className="blog-prose mx-auto max-w-3xl"
          dangerouslySetInnerHTML={{ __html: post.body_html }}
        />
      </section>

      {/* Tags */}
      {post.tags.length > 0 && (
        <section className="container mx-auto px-6 py-16">
          <div className="mx-auto flex max-w-3xl flex-wrap items-center gap-2 border-t border-white/8 pt-8">
            <span className="mr-1 text-sm font-medium text-white/40">Tagged</span>
            {post.tags.map((tag) => (
              <Link
                key={tag.slug}
                href={`/blogs/tag/${tag.slug}`}
                prefetch
                className="rounded-full border border-white/10 px-3 py-1 text-xs font-medium text-white/64 transition-colors hover:border-[#ff6a1a]/30 hover:text-[#ff6a1a]"
              >
                {tag.name}
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Related articles */}
      {related_posts.length > 0 && (
        <section className="container mx-auto px-6 py-20">
          <ScrollReveal>
            <h2 className="mb-8 text-center text-xl font-semibold text-white/90">More articles</h2>
          </ScrollReveal>
          <div className="mx-auto grid max-w-5xl gap-6 md:grid-cols-2 lg:grid-cols-3">
            {related_posts.map((rp, i) => (
              <ScrollReveal key={rp.slug} delay={i * 0.06}>
                <BlogPostCard post={rp} />
              </ScrollReveal>
            ))}
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="container mx-auto px-6 py-24">
        <ScrollReveal>
          <div className="marketing-final-cta mx-auto max-w-2xl text-center">
            <h2 className="text-2xl font-bold text-white">Ready to distribute your music?</h2>
            <p className="mt-3 text-white/64">
              Start your application and get your music on 100+ platforms. Plans from $15/year.
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <a href="/apply" className="marketing-button-primary">Apply Now</a>
              <a href="/pricing" className="marketing-button-secondary">See Plans</a>
            </div>
          </div>
        </ScrollReveal>
      </section>
    </>
  )
}
